/**
 * Webnovel Adapter Pattern
 * Bridges Firestore Data <-> Editor Internal State
 * Ensures robust data handling and zero data loss.
 */
import { serverTimestamp } from "firebase/firestore";

export const WebnovelAdapter = {
    /**
     * Converts Firestore Data to Editor Domain State
     * Ensures all fields are present to prevent undefined errors in UI
     */
    toDomain: (data) => {
        if (!data) return WebnovelAdapter.createEmpty();

        return {
            title: data.title || '',
            coverImage: data.coverImage || '',
            tags: Array.isArray(data.tags) ? data.tags : [],
            // AI Flag (Legacy compatibility: treat missing as false)
            isAI: !!data.isAI,

            // Chapter Normalization
            chapters: Array.isArray(data.chapters) ? data.chapters.map(WebnovelAdapter.normalizeChapter) : [],

            // Metadata Preservation (Read-only in Editor, but kept for save)
            isbn: data.isbn || '',
            uci: data.uci || '',
            authorId: data.authorId || null,
            createdAt: data.createdAt || null,
            status: data.status || 'draft', // Series status
            type: data.type || 'webnovel',

            // Stats (Read-only)
            viewCount: data.viewCount || 0,
            likeCount: data.likeCount || 0
        };
    },

    /**
     * Creates a guaranteed valid empty state
     */
    createEmpty: () => ({
        title: '',
        coverImage: '',
        tags: [],
        isAI: false,
        chapters: [{ ...WebnovelAdapter.createEmptyChapter(), title: 'Episode 1' }],
        isbn: '',
        uci: '',
        status: 'draft',
        type: 'webnovel'
    }),

    /**
     * Normalizes a single chapter object
     */
    normalizeChapter: (ch) => ({
        id: ch.id || null, // [FIX] Preserve ID to prevent ghost episodes
        title: ch.title || '',
        content: ch.content || '',
        image: ch.image || '', // Legacy image field (webnovel might use illustrations)
        note: ch.note || '',
        bgmUrl: ch.bgmUrl || '',
        status: ch.status || 'published', // Default to published for legacy compatibility unless explicitly draft
        viewCount: ch.viewCount || 0
    }),

    createEmptyChapter: () => ({
        title: '',
        content: '',
        image: '',
        note: '',
        bgmUrl: '',
        status: 'draft', // New chapters default to draft
        viewCount: 0
    }),

    /**
     * Prepares State for Firestore Save
     * performs sanitization and removal of UI-only flags
     */
    toFirestore: (state) => {
        const payload = {
            title: state.title.trim(),
            coverImage: state.coverImage.trim(),
            tags: state.tags.filter(t => t && t.trim().length > 0),
            isAI: !!state.isAI,
            status: state.status || 'draft', // [FIX] Ensure series status is saved
            updatedAt: serverTimestamp(),

            // Chapters
            chapters: state.chapters.map(ch => {
                const chapterData = {
                    title: ch.title.trim(),
                    content: ch.content,
                    image: ch.image || '',
                    note: ch.note || '',
                    bgmUrl: ch.bgmUrl ? ch.bgmUrl.trim() : '',
                    status: ch.status || 'draft'
                };

                // [FIX] Only include ID if it is truthy (valid string)
                // Firestore rejects 'undefined', so we must exclude the key entirely if missing.
                if (ch.id) {
                    chapterData.id = ch.id;
                }

                return chapterData;
            }),

            // Metadata Preservation
            isbn: state.isbn,
            uci: state.uci
        };

        // Remove undefined keys
        Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

        return payload;
    },

    /**
     * Validation before save
     */
    validate: (state) => {
        const errors = [];
        if (!state.title || state.title.trim().length === 0) {
            errors.push("시리즈 제목을 입력해주세요.");
        }
        if (state.chapters.length === 0) {
            errors.push("최소 1개의 에피소드가 필요합니다.");
        }

        // Check Chapter Titles
        state.chapters.forEach((ch, idx) => {
            if (!ch.title || ch.title.trim().length === 0) {
                errors.push(`에피소드 #${idx + 1}의 제목이 비어있습니다.`);
            }
        });

        return {
            isValid: errors.length === 0,
            errors
        };
    }
};
